import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, Validators, FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { BaseWizardComponent } from '../base-wizard.component';
import { IActivityStorageDataModel, IPurposeDataModel, IReasonModel, ReasonModel } from '../models/veg-wizard-models';
import { Router } from '@angular/router';
import { VegWizardDataService } from '../services/veg-wizard-data.service';
import { first } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PurposeEnum, QuestionReasonEnum, ActivityCodeEnum } from 'src/app/models/shared-models';
import { ReasonService } from '../services/reason.service';
import { UtilService } from '../services/util.service';
import { FeatureFlags } from 'src/app/services/feature-flag.service';
import { IfFeatureDisabledDirective } from 'src/app/core/directives/feature.directive';
import { GlobalValidationMessageComponent } from 'src/app/shared/global-validation-message/global-validation-message.component';
@Component({
  selector: 'app-reason',
  templateUrl: './reason.component.html',
  styleUrls: ['./reason.component.css'],
  imports: [
    ReactiveFormsModule,
    GlobalValidationMessageComponent
],
})

export class ReasonComponent extends BaseWizardComponent implements OnInit {
  form: FormGroup;
  nextButtonClicked = false;

  reasonDetails: IReasonModel[] | undefined;
  reasonExampleInformation = '';

  isCompletedCategoryOrSelfAudit = false;
  isReasonAnswerValid: boolean | undefined;
  isOtherReasonAnswerValid = false;
  isWoodChipSelected: boolean | undefined;
  isSilviCulturalSelected: boolean | undefined;
  isSilviCulturalPermited = true;

  displayReasonInputTextbox = false;
  purpose: IPurposeDataModel | undefined;
  activities: IActivityStorageDataModel | undefined;
  isNFPSelected: boolean | undefined;
  nfpReasonSelectionMade: boolean | undefined;

  questionCodes: string[] = [];
  questionOptions: QuestionReasonEnum[] = [];
  acceptedQuestions: boolean[] = [];
  checkInProgress = false;

  //from getters
  get reasonInputControl() { return this.form.controls['reasoninput']; }
  get questionOptionsControl() { return this.form.controls['questionOptions']; }
  get acceptedQuestionsControl() { return this.form.controls['acceptedQuestions']; }

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private reasonService: ReasonService,
    private utilService: UtilService,
    router: Router,

  ) {
    super();
    this.reasonDetails = this.vwds.getReason();
    this.purpose = this.vwds.getPurpose();
    this.activities = this.vwds.getActivities();

    this.isCompletedCategoryOrSelfAudit = this.featureFlag(FeatureFlags.NFP2019) &&
      ((this.vwds.getCategories() !== null && this.vwds.getCategories().length > 0) || (this.vwds.getSelfAuditEvidence() !== null));

    let reasonText = this.reasonDetails?.filter(r => r.code === QuestionReasonEnum.other)[0] ?? '';

    this.form = this.formBuilder.group({
      reasoninput: [reasonText],
      questionOptions: [this.questionOptions],
      acceptedQuestions: new FormArray([]),
    });
  }

  ngOnInit(): void {
    this.isReasonAnswerValid = (!this.featureFlag(FeatureFlags.NFP2019)) ? true : undefined;
    this.isNFPSelected = (this.purpose?.purpose.purposeCode === PurposeEnum.nativeForestPractice) ? true : false;

    if (this.featureFlag(FeatureFlags.NFP2019) && this.isNFPSelected) {
      this.getQuestionOptionsAndAcceptedQuestions();
      this.reasonExampleInformation = this.reasonService.getReasonExampleLabelInformation(this.purpose?.purpose, this.activities);
      this.checkValidSelections();

      this.form.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(value => {
          this.acceptedQuestions = value.acceptedQuestions;
          this.checkValidSelections();
        });
    } else {
      this.getQuestionOptionsAndAcceptedQuestions();
      this.reasonExampleInformation = this.reasonService.getReasonExampleLabelInformation(this.purpose?.purpose, this.activities);
      this.checkValidSelections();

      this.form.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(value => {
          if (value.questionOptions) {
            this.clearOptions();
            this.setOptionStateByQuestionText(value.questionOptions as QuestionReasonEnum, true);
            value.acceptedQuestions = this.acceptedQuestions;
          }

          this.acceptedQuestions = value.acceptedQuestions;
          this.checkValidSelections();
        });
    }
  }

  checkValidSelections() {
    //Prevent check from triggering more changes when in progress.
    if (this.checkInProgress)
      return;

    this.checkInProgress = true;
    this.isReasonAnswerValid = true;

    // clear out errors to start with
    this.questionOptionsControl?.setErrors(null);
    this.reasonInputControl?.setErrors(null);
    this.acceptedQuestionsControl?.setErrors(null);

    if (this.featureFlag(FeatureFlags.NFP2019)) {
      if (this.isNFPSelected) {
        //Needs to be done here so it will work when a user goes back to a previous page and selects a new activity.
        this.isSilviCulturalPermited = this.checkIfSilviCulturalPermited();

        if (this.checkOptionSelected(QuestionReasonEnum.other)) {
          this.displayReasonInputTextbox = true;
        } else {
          this.form.controls['reasoninput'].setValue('');
          this.displayReasonInputTextbox = false;
        }

        this.isWoodChipSelected = this.checkOptionSelected(QuestionReasonEnum.woodChipsForTheExportMarket);
        this.isSilviCulturalSelected = this.checkOptionSelected(QuestionReasonEnum.silviculturalTreatment);

        if (this.isWoodChipSelected) {
          this.isReasonAnswerValid = false;
        }

        if (this.isSilviCulturalSelected && !this.isSilviCulturalPermited) {
          this.isReasonAnswerValid = false;
        }

        if (this.acceptedQuestions.every(v => v === false)) {
          this.isReasonAnswerValid = undefined;
        }

        this.nfpReasonSelectionMade = (this.acceptedQuestions.some(v => v === true) && this.isNFPSelected) ? true : false;
        if (!this.nfpReasonSelectionMade) {
          this.acceptedQuestionsControl?.setErrors({ 'Required': true });
        }

        if (this.checkOptionSelected(QuestionReasonEnum.other)) {
          if (this.form.controls['reasoninput'].value === '') {
            this.isReasonAnswerValid = false;
            this.isOtherReasonAnswerValid = false;
          } else {
            if (this.isReasonAnswerValid != false)
              this.isReasonAnswerValid = true;
            this.isOtherReasonAnswerValid = true;
          }
        }
      }
      else if (!this.isNFPSelected) { // NFP purpose is not selected

        this.displayReasonInputTextbox = true;

        if (this.displayReasonInputTextbox && this.form.controls['reasoninput']) {
          if (this.form.controls['reasoninput'].value === '') {
            this.isReasonAnswerValid = false;
            this.reasonInputControl?.setErrors({ 'Required': true });
          }
        }
      }

      if (this.isReasonAnswerValid) { this.questionOptionsControl.setErrors(null); }
      else if (this.isOtherReasonAnswerValid == false) { this.reasonInputControl?.setErrors({ 'Not Valid': true }); }
      else if (this.isReasonAnswerValid == false) { this.questionOptionsControl.setErrors({ required: true }); }
      else if (this.isReasonAnswerValid == undefined) { this.questionOptionsControl.setErrors({ required: true }); }
      this.checkInProgress = false;
    }
    // NFP2019 feature flag off
    else {
      this.displayReasonInputTextbox = true;

      if (this.displayReasonInputTextbox && this.form.controls['reasoninput']) {
        if (this.form.controls['reasoninput'].value === '') {
          this.isReasonAnswerValid = false;
          this.reasonInputControl?.setErrors({ 'Required': true });
        }
      }

      if (this.isReasonAnswerValid === false) {
        this.questionOptionsControl.setErrors({ invalid: "Please read the notice below!" });
      } else if (this.isReasonAnswerValid === undefined) {
        this.questionOptionsControl.setErrors({ required: true });
      }
      this.checkInProgress = false;
    }
  }

  checkOptionSelected(question: QuestionReasonEnum): boolean {
    const index = this.questionOptions.findIndex(option => option === question);
    return this.acceptedQuestions[index];
  }

  setOptionState(question: QuestionReasonEnum, value: boolean) {
    const index = this.questionOptions.findIndex(option => option == question);
    this.acceptedQuestions[index] = value;
  }
  clearOptions() {
    this.acceptedQuestions = new Array(this.questionOptions.length).fill(false);
  }

  getEnumFromCode(code: string): QuestionReasonEnum {
    const lowerCode = code.charAt(0).toLowerCase() + code.slice(1);
    const key = (lowerCode) as keyof typeof QuestionReasonEnum;
    return QuestionReasonEnum[key];
  }

  setOptionStateByQuestionText(text: QuestionReasonEnum, value: boolean) {
    const index = this.questionOptions.findIndex(option => option === text);
    this.acceptedQuestions[index] = value;
  }

  checkIfSilviCulturalPermited(): boolean {
    const activities = this.vwds.getActivities();

    if (activities === null || activities === undefined)
      return true;

    for (let activity of activities!.selectedActivities!) {
      switch (activity.activityCode) {
        case ActivityCodeEnum.selectiveHarvestingInHardwoodREs:
        case ActivityCodeEnum.sourcingTimberInfrastructure:
        case ActivityCodeEnum.specialityTimberHarvesting:
        case ActivityCodeEnum.selectiveHarvestingInDenseREs:
        case ActivityCodeEnum.groupSelectionHarvesting:
          return false;
      }
    }
    return true;
  }
  override next(): void {
    this.nextButtonClicked = true;

    let model: ReasonModel[] = new Array();
    this.checkValidSelections();

    // do a final form validation checks
    if (this.form.invalid) {
      this.utilService.getFormValidationErrors(this.form);
      return;
    }

    if (this.featureFlag(FeatureFlags.NFP2019)) {

      if (this.isNFPSelected) {
        for (let i = 0; i < this.acceptedQuestions.length; i++) {
          if (this.acceptedQuestions[i]) {
            if (this.questionCodes[i] === QuestionReasonEnum.other) {
              model.push({ code: this.questionCodes[i], text: this.reasonInputControl?.value ?? '' });
            } else {
              model.push({ code: this.questionCodes[i], text: '' })
            }
          }
        }
      }
      else if (!this.isNFPSelected) {
        if (this.isReasonAnswerValid) {
          model = [];
          model.push({ code: QuestionReasonEnum.other, text: this.reasonInputControl?.value ?? '' });
        }
      }
    }
    else {

      if (this.displayReasonInputTextbox) {
        this.setOptionState(QuestionReasonEnum.other, true);
      }

      for (let i = 0; i < this.acceptedQuestions.length; i++) {
        if (this.acceptedQuestions[i]) {
          if (this.questionCodes[i] === QuestionReasonEnum.other) {
            model.push({ code: this.questionCodes[i], text: this.reasonInputControl?.value ?? '' });
          } else {
            model.push({ code: this.questionCodes[i], text: '' })
          }
        }
      }
    }

    this.vwds
      .setReason$(model)
      .pipe(first(), untilDestroyed(this))
      .subscribe((success) => {
        if (success) {
          super.next();
        }
      });
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
  getQuestionOptionsAndAcceptedQuestions() {
    this.questionOptions = Object.values(QuestionReasonEnum);
    this.questionCodes = Object.keys(QuestionReasonEnum).map(c => c[0].toUpperCase() + c.substring(1));
    this.acceptedQuestions = new Array(this.questionOptions.length).fill(false);

    if (this.reasonDetails && this.reasonDetails.length > 0) {
      for (let reason of this.reasonDetails) {

        this.setOptionState(this.getEnumFromCode(reason.code), true);

        if (reason.code == QuestionReasonEnum.other) {
          this.form.controls['reasoninput'].setValue(reason.text);
          this.displayReasonInputTextbox = true;
        }
      }
    }

    //If we are not in multi select mode clear other as we are only using it for the reason text.
    if (!this.isNFPSelected && this.acceptedQuestions.filter(x => x).length > 1) {
      this.setOptionState(QuestionReasonEnum.other, false);
    }

    //When nfp is not shipped the only option is the weed reason box.
    if (!this.featureFlag(FeatureFlags.NFP2019) || !this.isNFPSelected) {
      this.displayReasonInputTextbox = true;
    }

    this.acceptedQuestions.forEach((x, i) => {
      if (x) {
        this.questionOptionsControl.setValue(this.questionOptions[i]);
      }

      const control = new FormControl(x);
      (this.form.controls['acceptedQuestions'] as FormArray).push(control);
    });
  }

}
